import { combineReducers, configureStore } from '@reduxjs/toolkit';

import { actsAndDocumentsSlice } from '@/features/actsAndDocuments/actsAndDocumentsSlice';
import { actualExpenseTransferApi } from '@/features/actualExpenseTransfer/actualExpenseTransferApi';
import { actualExpensesApi } from '@/features/actualExpenses/actualExpensesApi';
import { admissionClassesApi } from '@/features/admissionClasses/admissionClassesApi';
import { allocationTypesApi } from '@/features/allocationTypes/allocationTypesApi';
import { annualOrderedCapacitiesApi } from '@/features/annualOrderedCapacities/annualOrderedCapacitiesApi';
import { apiKeysApi } from '@/features/apiKeys/apiKeysApi';
import { appSettingsApi } from '@/features/appSettings/appSettingsApi';
import { appendix4Api } from '@/features/appendix4/appendix4Api';
import { auditApi } from '@/features/audit/auditApi';
import { authApi } from '@/features/auth/authApi';
import authSlice from '@/features/auth/authSlice';
import { balanceActsApi } from '@/features/balanceActs';
import { bridgeActsApi } from '@/features/bridgeActs/bridgeActsApi';
import { commercialMeteringPointsApi } from '@/features/commercialMeteringPoint/commercialMeteringPointsApi';
import { commercialMeteringPointAdjustmentApi } from '@/features/commercialMeteringPointAdjustment/commercialMeteringPointAdjustmentApi';
import { commercialMeteringPointOwnershipsApi } from '@/features/commercialMeteringPointOwnerships/commercialMeteringPointOwnershipsApi';
import { commercialMeteringPointTechnicalCalculationApi } from '@/features/commercialMeteringPointTechnicalCalculation/commercialMeteringPointTechnicalCalculationApi';
import { connectedPowerCalculatorApi } from '@/features/connectedPowerCalculator/connectedPowerCalculatorApi';
import { consumerDocumentsApi } from '@/features/consumerDocuments/consumerDocumentsApi';
import { consumptionCategoryKindsApi } from '@/features/consumptionCategoryKinds/consumptionCategoryKindsApi';
import { contractsApi } from '@/features/contracts/contractsApi';
import { counterpartRequestsApi } from '@/features/counterpartRequests/counterpartRequestsApi';
import { counterpartiesApi } from '@/features/counterparties/counterparties';
import { counterpartyActualExpenseIndicatorApi } from '@/features/counterpartyActualExpenseIndicator/counterpartyActualExpenseIndicatorApi';
import { counterpartyContactPersonsApi } from '@/features/counterpartyContactPersons/counterpartyContactPersonsApi';
import { counterpartyDocumentTypesAp } from '@/features/counterpartyDocumentTypes/counterpartyDocumentTypesApi';
import { counterpartyPositionsApi } from '@/features/counterpartyPositions/counterpartyPositionsApi';
import { departmentsApi } from '@/features/departments/departmentsApi';
import { deviceStatesApi } from '@/features/deviceStates/deviceStatesApi';
import { disconnectionNotificationApi } from '@/features/disconnectionNotification/disconnectionNotificationApi';
import { disconnectionReasonsApi } from '@/features/disconnectionReasons/disconnectionReasonsApi';
import { distributionTariffsApi } from '@/features/distributionTariffs/distributionTariffsApi';
import { economicSectorObjectTypesApi } from '@/features/economicSectorObjectTypes/economicSectorObjectTypesApi';
import { economicSectorsApi } from '@/features/economicSectors/economicSectorsApi';
import { employeePositionApi } from '@/features/employeePositions/employeePositionsApi';
import { employeesApi } from '@/features/employees/employeesApi';
import { equipmentGroupApi } from '@/features/equipmentGroupApi';
import { equipmentManufacturerApi } from '@/features/equipmentManufacturerApi';
import { equipmentTypeApi } from '@/features/equipmentTypeApi';
import { executionResultsApi } from '@/features/executionResults/executionResultsApi';
import { formNineApi } from '@/features/formNine/formNineApi';
import { gasConsumingEquipmentApi } from '@/features/gasConsumingEquipmentApi';
import { gasConsumingEquipmentCommercialApi } from '@/features/gasConsumingEquipmentCommercialApi';
import { gasDistributionReportsApi } from '@/features/gasDistributionReports/gasDistributionReportsApi';
import { gasDistributionStationCategoryApi } from '@/features/gasDistributionStationCategory';
import { gasDistributionStationTypeApi } from '@/features/gasDistributionStationType';
import { gasDistributionStationsApi } from '@/features/gasDistributionStations/gasDistributionStationsApi';
import { gasPipelineSectionGroupsApi } from '@/features/gasPipelineSectionGroups';
import { gasPipelineSectionLocationsApi } from '@/features/gasPipelineSectionLocations';
import { gasPipelineSectionTypesApi } from '@/features/gasPipelineSectionTypes';
import { gasPipelineSectionsApi } from '@/features/gasPipelineSectionsApi';
import { gasRoutePhysicoChemicalIndicatorApi } from '@/features/gasRoutePhysicoChemicalIndicator/gasRoutePhysicoChemicalIndicatorApi';
import { gasRouteTypesApi } from '@/features/gasRouteTypes/gasRouteTypesApi';
import { gasRoutesApi } from '@/features/gasRoutes/gasRoutesApi';
import { gasSuppliersApi } from '@/features/gasSuppliers/gasSuppliersApi';
import { gasThreadApi } from '@/features/gasThread/gasThreadApi';
import { gasThreadPhysicoChemicalIndicatorApi } from '@/features/gasThreadPhysicoChemicalIndicator/gasThreadPhysicoChemicalIndicatorApi';
import { gdsPhysicoChemicalIndicatorApi } from '@/features/gdsPhysicoChemicalIndicator/gdsPhysicoChemicalIndicatorApi';
import { guaranteeLettersApi } from '@/features/guaranteeLetters/guaranteeLettersApi';
import { lastResortSuppliersApi } from '@/features/lastResortSuppliers/lastResortSuppliersApi';
import { locationsApi } from '@/features/locations/locationsApi';
import { mainOrganizationApi } from '@/features/mainOrganization/mainOrganizationApi';
import { measurementPeriodsApi } from '@/features/measurementPeriods/measurementPeriodsApi';
import { measurementRangesApi } from '@/features/measurementRanges/measurementRangesApi';
import { meterAccuracyClassesApi } from '@/features/meterAccuracyClasses/meterAccuracyClassesApi';
import { meterKindsApi } from '@/features/meterKinds/meterKindsApi';
import { meterLocationsApi } from '@/features/meterLocations/meterLocationsApi';
import { meterThroughputsApi } from '@/features/meterThroughputs/meterThroughputsApi';
import { meterTypesApi } from '@/features/meterTypes/meterTypesApi';
import { meteringDeviceConsumerIndicatorApi } from '@/features/meteringDeviceConsumerIndicator/meteringDeviceConsumerIndicatorApi';
import { meteringDeviceGroupsApi } from '@/features/meteringDeviceGroups/meteringDevicePassportTypeApi';
import { meteringDeviceManufacturersAPI } from '@/features/meteringDeviceManufacturers/meteringDeviceManufacturersAPI';
import { meteringDevicePassportTypeApi } from '@/features/meteringDevicePassportType/meteringDevicePassportTypeApi';
import { meteringDeviceTypeApi } from '@/features/meteringDeviceType/meteringDeviceTypeApi';
import { meteringDevicesApi } from '@/features/meteringDevicesApi';
import { meteringDevicesCommercialApi } from '@/features/meteringDevicesCommercial';
import { meteringModemApi } from '@/features/meteringModem/meteringModemApi';
import { meteringModemCommercialApi } from '@/features/meteringModemCommercial';
import { meteringModemTypeApi } from '@/features/meteringModemType/meteringModemTypeApi';
import { meteringNodeApi } from '@/features/meteringNode/meteringNodeApi';
import { meteringNodeCommercialApi } from '@/features/meteringNodeCommercial';
import { meteringNodeKindApi } from '@/features/meteringNodeKind/meteringNodeKindApi';
import { meteringNodeLocationTypeApi } from '@/features/meteringNodeLocationType/meteringNodeLocationTypeApi';
import { meteringNodeTypeApi } from '@/features/meteringNodeType/meteringNodeTypeApi';
import { meteringPointApi } from '@/features/meteringPoint/meteringPointApi';
import { meteringPointActStepValidationApi } from '@/features/meteringPointActStepValidation/meteringPointActStepValidationApi';
import { meteringPointActTypesApi } from '@/features/meteringPointActTypes/meteringPointActTypesApi';
import { meteringPointActionsApi } from '@/features/meteringPointActions/meteringPointActionsApi';
import { meteringPointActApi } from '@/features/meteringPointActs/meteringPointActApi';
import { meteringPointActualIncomeApi } from '@/features/meteringPointActualIncome';
import { meteringPointActualIncomeIndicatorsApi } from '@/features/meteringPointActualIncomeIndicators';
import { meteringPointAdjustmentApi } from '@/features/meteringPointAdjustment/meteringPointAdjustmentApi';
import { meteringPointConnectionTypesApi } from '@/features/meteringPointConnectionTypes/meteringPointConnectionTypesApi';
import { meteringPointDisconnectionReasonsApi } from '@/features/meteringPointDisconnectionReasons/meteringPointDisconnectionReasonsApi';
import { meteringPointOwnDemandApi } from '@/features/meteringPointOwnDemand/meteringPointOwnDemandApi';
import { meteringPointProjectsApi } from '@/features/meteringPointProjects';
import { meteringPointStatusesApi } from '@/features/meteringPointStatuses/meteringPointStatusesApi';
import { meteringPointTechnicalCalculationApi } from '@/features/meteringPointTechnicalCalculation/meteringPointTechnicalCalculationApi';
import { meteringPointTypeApi } from '@/features/meteringPointType/meteringPointApi';
import { meteringSealApi } from '@/features/meteringSealApi';
import { meteringSealCommercialApi } from '@/features/meteringSealCommercial';
import { networkTypeApi } from '@/features/networkType/networkTypeApi';
import { nodeSealLocationApi } from '@/features/nodeSealLocation/nodeSealLocationApi';
import { nodeSealTypesApi } from '@/features/nodeSealTypes/nodeSealTypesApi';
import { notificationTypesApi } from '@/features/notificationTypes/notificationTypesApi';
import { operatingGasRoutesApi } from '@/features/operatingGasRoutes/operatingGasRoutesApi';
import { operatingIncomesApi } from '@/features/operatingIncomes/operatingIncomesApi';
import { operationalExpenseApi } from '@/features/operationalExpense';
import { orderedCapacityExcessesApi } from '@/features/orderedCapacityExcesses/orderedCapacityExcessesApi';
import { orderedCapacityFinalExcessApi } from '@/features/orderedCapacityFinalExcess/orderedCapacityFinalExcessApi';
import { organizationsApi } from '@/features/organizations/organizationsApi';
import { outageTypesApi } from '@/features/outageTypes/outageTypesApi';
import { ownDemandTypeApi } from '@/features/ownDemandType/ownDemandTypeApi';
import { permissionsApi } from '@/features/permissions/permissionsApi';
import { pressureCategoriesApi } from '@/features/pressureCategories';
import { projectDocumentationAvailabilitiesApi } from '@/features/projectDocumentationAvailabilities/projectDocumentationAvailabilitiesApi';
import { projectKindsApi } from '@/features/projectKinds/projectKindsApi';
import { referenceBooksApi } from '@/features/referenceBooks';
import { reportSourceTypesApi } from '@/features/reportSourceTypes/reportSourceTypesApi';
import { reportTemplatesApi } from '@/features/reportTemplates/reportTemplatesApi';
import { reportsApi } from '@/features/reports/reportsApi';
import { rolesApi } from '@/features/roles/rolesApi';
import { seasonTypesApi } from '@/features/seasonTypes/seasonTypesApi';
import { sectorBridgesApi } from '@/features/sectorBridges/sectorBridgesApi';
import { sectorsApi } from '@/features/sectors/sectorsApi';
import { serviceOrganizationsApi } from '@/features/serviceOrganizationsApi';
import { ssoIntegrationsApi } from '@/features/ssoIntegrations/ssoIntegrationsApi';
import { subdivisionsApi } from '@/features/subdivisions/subdivisionsApi';
import subsystemsApi from '@/features/subsystems/subsystemsApi';
import { technicalConditionsApi } from '@/features/technicalConditions/technicalConditionsApi';
import { technicalConditionsActTypesApi } from '@/features/technicalConditionsActTypes/technicalConditionsActTypesApi';
import { transportOrganizationApi } from '@/features/transportOrganization/transportOrganizationApi';
import uiSlice from '@/features/ui/uiSlice';
import { usersApi } from '@/features/users/usersApi';
import usersSlice from '@/features/users/usersSlice';
import { violationActsApi } from '@/features/violationActs/violationActsApi';
import { violationDecisionsApi } from '@/features/violationDecisions/violationDecisionsApi';
import { violationMeasuresApi } from '@/features/violationMeasures/violationMeasuresApi';
import { violationResultsApi } from '@/features/violationResults/violationResultsApi';
import { violationTypesApi } from '@/features/violationTypes/violationTypesApi';
import workingPeriodsApi from '@/features/workingPeriods/workingPeriodsApi';

const rootReducer = combineReducers({
  [uiSlice.name]: uiSlice.reducer,
  [appSettingsApi.reducerPath]: appSettingsApi.reducer,
  [authSlice.name]: authSlice.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [organizationsApi.reducerPath]: organizationsApi.reducer,
  [subdivisionsApi.reducerPath]: subdivisionsApi.reducer,
  [employeesApi.reducerPath]: employeesApi.reducer,
  [counterpartiesApi.reducerPath]: counterpartiesApi.reducer,
  [meteringNodeApi.reducerPath]: meteringNodeApi.reducer,
  [meteringNodeTypeApi.reducerPath]: meteringNodeTypeApi.reducer,
  [meteringNodeLocationTypeApi.reducerPath]:
    meteringNodeLocationTypeApi.reducer,
  [seasonTypesApi.reducerPath]: seasonTypesApi.reducer,
  [meteringNodeKindApi.reducerPath]: meteringNodeKindApi.reducer,
  [commercialMeteringPointsApi.reducerPath]:
    commercialMeteringPointsApi.reducer,
  [usersSlice.name]: usersSlice.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [gasDistributionStationsApi.reducerPath]: gasDistributionStationsApi.reducer,
  [gasDistributionStationTypeApi.reducerPath]:
    gasDistributionStationTypeApi.reducer,
  [gasDistributionStationCategoryApi.reducerPath]:
    gasDistributionStationCategoryApi.reducer,
  [gasThreadApi.reducerPath]: gasThreadApi.reducer,
  [networkTypeApi.reducerPath]: networkTypeApi.reducer,
  [meteringPointApi.reducerPath]: meteringPointApi.reducer,
  [meteringPointTypeApi.reducerPath]: meteringPointTypeApi.reducer,
  [sectorsApi.reducerPath]: sectorsApi.reducer,
  [sectorBridgesApi.reducerPath]: sectorBridgesApi.reducer,
  [bridgeActsApi.reducerPath]: bridgeActsApi.reducer,
  [ownDemandTypeApi.reducerPath]: ownDemandTypeApi.reducer,
  [locationsApi.reducerPath]: locationsApi.reducer,
  [gasRoutesApi.reducerPath]: gasRoutesApi.reducer,
  [gasRouteTypesApi.reducerPath]: gasRouteTypesApi.reducer,
  [operatingGasRoutesApi.reducerPath]: operatingGasRoutesApi.reducer,
  [transportOrganizationApi.reducerPath]: transportOrganizationApi.reducer,
  [mainOrganizationApi.reducerPath]: mainOrganizationApi.reducer,
  [meteringSealApi.reducerPath]: meteringSealApi.reducer,
  [nodeSealTypesApi.reducerPath]: nodeSealTypesApi.reducer,
  [nodeSealLocationApi.reducerPath]: nodeSealLocationApi.reducer,
  [meteringDevicesApi.reducerPath]: meteringDevicesApi.reducer,
  [meteringModemApi.reducerPath]: meteringModemApi.reducer,
  [meteringModemTypeApi.reducerPath]: meteringModemTypeApi.reducer,
  [consumptionCategoryKindsApi.reducerPath]:
    consumptionCategoryKindsApi.reducer,
  [economicSectorObjectTypesApi.reducerPath]:
    economicSectorObjectTypesApi.reducer,
  [measurementPeriodsApi.reducerPath]: measurementPeriodsApi.reducer,
  [pressureCategoriesApi.reducerPath]: pressureCategoriesApi.reducer,
  [rolesApi.reducerPath]: rolesApi.reducer,
  [meteringDevicePassportTypeApi.reducerPath]:
    meteringDevicePassportTypeApi.reducer,
  [meteringDeviceTypeApi.reducerPath]: meteringDeviceTypeApi.reducer,
  [admissionClassesApi.reducerPath]: admissionClassesApi.reducer,
  [meterLocationsApi.reducerPath]: meterLocationsApi.reducer,
  [measurementRangesApi.reducerPath]: measurementRangesApi.reducer,
  [meterThroughputsApi.reducerPath]: meterThroughputsApi.reducer,
  [meteringDeviceGroupsApi.reducerPath]: meteringDeviceGroupsApi.reducer,
  [gasConsumingEquipmentApi.reducerPath]: gasConsumingEquipmentApi.reducer,
  [equipmentGroupApi.reducerPath]: equipmentGroupApi.reducer,
  [equipmentManufacturerApi.reducerPath]: equipmentManufacturerApi.reducer,
  [equipmentTypeApi.reducerPath]: equipmentTypeApi.reducer,
  [contractsApi.reducerPath]: contractsApi.reducer,
  [serviceOrganizationsApi.reducerPath]: serviceOrganizationsApi.reducer,
  [meteringPointStatusesApi.reducerPath]: meteringPointStatusesApi.reducer,
  [formNineApi.reducerPath]: formNineApi.reducer,
  [operatingIncomesApi.reducerPath]: operatingIncomesApi.reducer,
  [disconnectionReasonsApi.reducerPath]: disconnectionReasonsApi.reducer,
  [commercialMeteringPointOwnershipsApi.reducerPath]:
    commercialMeteringPointOwnershipsApi.reducer,
  [meteringPointActApi.reducerPath]: meteringPointActApi.reducer,
  [meteringPointActTypesApi.reducerPath]: meteringPointActTypesApi.reducer,
  [counterpartRequestsApi.reducerPath]: counterpartRequestsApi.reducer,
  [counterpartyDocumentTypesAp.reducerPath]:
    counterpartyDocumentTypesAp.reducer,
  [gasPipelineSectionsApi.reducerPath]: gasPipelineSectionsApi.reducer,
  [gasRoutePhysicoChemicalIndicatorApi.reducerPath]:
    gasRoutePhysicoChemicalIndicatorApi.reducer,
  [gdsPhysicoChemicalIndicatorApi.reducerPath]:
    gdsPhysicoChemicalIndicatorApi.reducer,
  [gasThreadPhysicoChemicalIndicatorApi.reducerPath]:
    gasThreadPhysicoChemicalIndicatorApi.reducer,
  [meteringPointActionsApi.reducerPath]: meteringPointActionsApi.reducer,
  [meteringPointDisconnectionReasonsApi.reducerPath]:
    meteringPointDisconnectionReasonsApi.reducer,
  [meteringPointConnectionTypesApi.reducerPath]:
    meteringPointConnectionTypesApi.reducer,
  [outageTypesApi.reducerPath]: outageTypesApi.reducer,
  [deviceStatesApi.reducerPath]: deviceStatesApi.reducer,
  [connectedPowerCalculatorApi.reducerPath]:
    connectedPowerCalculatorApi.reducer,
  [actsAndDocumentsSlice.name]: actsAndDocumentsSlice.reducer,
  [operationalExpenseApi.reducerPath]: operationalExpenseApi.reducer,
  [meteringPointActStepValidationApi.reducerPath]:
    meteringPointActStepValidationApi.reducer,
  [balanceActsApi.reducerPath]: balanceActsApi.reducer,
  [gasPipelineSectionLocationsApi.reducerPath]:
    gasPipelineSectionLocationsApi.reducer,
  [gasPipelineSectionTypesApi.reducerPath]: gasPipelineSectionTypesApi.reducer,
  [gasPipelineSectionGroupsApi.reducerPath]:
    gasPipelineSectionGroupsApi.reducer,
  [allocationTypesApi.reducerPath]: allocationTypesApi.reducer,
  [meteringPointTechnicalCalculationApi.reducerPath]:
    meteringPointTechnicalCalculationApi.reducer,
  [meteringPointAdjustmentApi.reducerPath]: meteringPointAdjustmentApi.reducer,
  [meteringPointActualIncomeApi.reducerPath]:
    meteringPointActualIncomeApi.reducer,
  [meteringPointOwnDemandApi.reducerPath]: meteringPointOwnDemandApi.reducer,
  [meteringPointActualIncomeIndicatorsApi.reducerPath]:
    meteringPointActualIncomeIndicatorsApi.reducer,
  [actualExpensesApi.reducerPath]: actualExpensesApi.reducer,
  [counterpartyActualExpenseIndicatorApi.reducerPath]:
    counterpartyActualExpenseIndicatorApi.reducer,
  [commercialMeteringPointTechnicalCalculationApi.reducerPath]:
    commercialMeteringPointTechnicalCalculationApi.reducer,
  [commercialMeteringPointAdjustmentApi.reducerPath]:
    commercialMeteringPointAdjustmentApi.reducer,
  [annualOrderedCapacitiesApi.reducerPath]: annualOrderedCapacitiesApi.reducer,
  [permissionsApi.reducerPath]: permissionsApi.reducer,
  [technicalConditionsApi.reducerPath]: technicalConditionsApi.reducer,
  [projectDocumentationAvailabilitiesApi.reducerPath]:
    projectDocumentationAvailabilitiesApi.reducer,
  [projectKindsApi.reducerPath]: projectKindsApi.reducer,
  [technicalConditionsActTypesApi.reducerPath]:
    technicalConditionsActTypesApi.reducer,
  [meteringPointProjectsApi.reducerPath]: meteringPointProjectsApi.reducer,
  [notificationTypesApi.reducerPath]: notificationTypesApi.reducer,
  [economicSectorsApi.reducerPath]: economicSectorsApi.reducer,
  [gasSuppliersApi.reducerPath]: gasSuppliersApi.reducer,
  [reportsApi.reducerPath]: reportsApi.reducer,
  [reportSourceTypesApi.reducerPath]: reportSourceTypesApi.reducer,
  [disconnectionNotificationApi.reducerPath]:
    disconnectionNotificationApi.reducer,
  [guaranteeLettersApi.reducerPath]: guaranteeLettersApi.reducer,
  [executionResultsApi.reducerPath]: executionResultsApi.reducer,
  [meteringDevicesCommercialApi.reducerPath]:
    meteringDevicesCommercialApi.reducer,
  [meteringModemCommercialApi.reducerPath]: meteringModemCommercialApi.reducer,
  [gasConsumingEquipmentCommercialApi.reducerPath]:
    gasConsumingEquipmentCommercialApi.reducer,
  [meteringNodeCommercialApi.reducerPath]: meteringNodeCommercialApi.reducer,
  [meteringSealCommercialApi.reducerPath]: meteringSealCommercialApi.reducer,
  [violationDecisionsApi.reducerPath]: violationDecisionsApi.reducer,
  [violationMeasuresApi.reducerPath]: violationMeasuresApi.reducer,
  [violationResultsApi.reducerPath]: violationResultsApi.reducer,
  [violationTypesApi.reducerPath]: violationTypesApi.reducer,
  [violationActsApi.reducerPath]: violationActsApi.reducer,
  [counterpartyContactPersonsApi.reducerPath]:
    counterpartyContactPersonsApi.reducer,
  [counterpartyPositionsApi.reducerPath]: counterpartyPositionsApi.reducer,
  [orderedCapacityExcessesApi.reducerPath]: orderedCapacityExcessesApi.reducer,
  [gasDistributionReportsApi.reducerPath]: gasDistributionReportsApi.reducer,
  [meteringDeviceConsumerIndicatorApi.reducerPath]:
    meteringDeviceConsumerIndicatorApi.reducer,
  [distributionTariffsApi.reducerPath]: distributionTariffsApi.reducer,
  [lastResortSuppliersApi.reducerPath]: lastResortSuppliersApi.reducer,
  [actualExpenseTransferApi.reducerPath]: actualExpenseTransferApi.reducer,
  [consumerDocumentsApi.reducerPath]: consumerDocumentsApi.reducer,
  [appendix4Api.reducerPath]: appendix4Api.reducer,
  [orderedCapacityFinalExcessApi.reducerPath]:
    orderedCapacityFinalExcessApi.reducer,
  [apiKeysApi.reducerPath]: apiKeysApi.reducer,
  [ssoIntegrationsApi.reducerPath]: ssoIntegrationsApi.reducer,
  [workingPeriodsApi.reducerPath]: workingPeriodsApi.reducer,
  [auditApi.reducerPath]: auditApi.reducer,
  [subsystemsApi.reducerPath]: subsystemsApi.reducer,
  [referenceBooksApi.reducerPath]: referenceBooksApi.reducer,
  [reportTemplatesApi.reducerPath]: reportTemplatesApi.reducer,
  [employeePositionApi.reducerPath]: employeePositionApi.reducer,
  [departmentsApi.reducerPath]: departmentsApi.reducer,
  [meteringDeviceManufacturersAPI.reducerPath]:
    meteringDeviceManufacturersAPI.reducer,
  [meterTypesApi.reducerPath]: meterTypesApi.reducer,
  [meterKindsApi.reducerPath]: meterKindsApi.reducer,
  [meterAccuracyClassesApi.reducerPath]: meterAccuracyClassesApi.reducer,
});

const middlewares = [
  appSettingsApi.middleware,
  authApi.middleware,
  organizationsApi.middleware,
  subdivisionsApi.middleware,
  usersApi.middleware,
  gasDistributionStationsApi.middleware,
  gasDistributionStationTypeApi.middleware,
  gasDistributionStationCategoryApi.middleware,
  gasThreadApi.middleware,
  networkTypeApi.middleware,
  meteringPointApi.middleware,
  meteringPointTypeApi.middleware,
  sectorsApi.middleware,
  gasPipelineSectionLocationsApi.middleware,
  gasPipelineSectionTypesApi.middleware,
  sectorBridgesApi.middleware,
  bridgeActsApi.middleware,
  ownDemandTypeApi.middleware,
  locationsApi.middleware,
  employeesApi.middleware,
  counterpartiesApi.middleware,
  meteringNodeApi.middleware,
  gasRoutesApi.middleware,
  gasRouteTypesApi.middleware,
  operatingGasRoutesApi.middleware,
  transportOrganizationApi.middleware,
  seasonTypesApi.middleware,
  meteringNodeKindApi.middleware,
  meteringNodeTypeApi.middleware,
  meteringNodeLocationTypeApi.middleware,
  rolesApi.middleware,
  mainOrganizationApi.middleware,
  meteringSealApi.middleware,
  nodeSealTypesApi.middleware,
  nodeSealLocationApi.middleware,
  meteringDevicesApi.middleware,
  meteringModemApi.middleware,
  meteringModemTypeApi.middleware,
  commercialMeteringPointsApi.middleware,
  consumptionCategoryKindsApi.middleware,
  economicSectorObjectTypesApi.middleware,
  measurementPeriodsApi.middleware,
  pressureCategoriesApi.middleware,
  serviceOrganizationsApi.middleware,
  meteringDevicePassportTypeApi.middleware,
  meteringDeviceTypeApi.middleware,
  admissionClassesApi.middleware,
  meterLocationsApi.middleware,
  measurementRangesApi.middleware,
  meterThroughputsApi.middleware,
  meteringDeviceGroupsApi.middleware,
  gasConsumingEquipmentApi.middleware,
  equipmentGroupApi.middleware,
  equipmentManufacturerApi.middleware,
  equipmentTypeApi.middleware,
  contractsApi.middleware,
  meteringPointStatusesApi.middleware,
  formNineApi.middleware,
  commercialMeteringPointOwnershipsApi.middleware,
  operatingIncomesApi.middleware,
  meteringPointActApi.middleware,
  meteringPointActTypesApi.middleware,
  counterpartRequestsApi.middleware,
  counterpartyDocumentTypesAp.middleware,
  disconnectionReasonsApi.middleware,
  gasPipelineSectionsApi.middleware,
  gasRoutePhysicoChemicalIndicatorApi.middleware,
  gdsPhysicoChemicalIndicatorApi.middleware,
  gasThreadPhysicoChemicalIndicatorApi.middleware,
  meteringPointActionsApi.middleware,
  meteringPointDisconnectionReasonsApi.middleware,
  meteringPointConnectionTypesApi.middleware,
  outageTypesApi.middleware,
  deviceStatesApi.middleware,
  connectedPowerCalculatorApi.middleware,
  operationalExpenseApi.middleware,
  meteringPointActStepValidationApi.middleware,
  balanceActsApi.middleware,
  gasPipelineSectionGroupsApi.middleware,
  allocationTypesApi.middleware,
  meteringPointTechnicalCalculationApi.middleware,
  meteringPointAdjustmentApi.middleware,
  meteringPointActualIncomeApi.middleware,
  meteringPointOwnDemandApi.middleware,
  meteringPointActualIncomeIndicatorsApi.middleware,
  actualExpensesApi.middleware,
  counterpartyActualExpenseIndicatorApi.middleware,
  commercialMeteringPointTechnicalCalculationApi.middleware,
  commercialMeteringPointAdjustmentApi.middleware,
  annualOrderedCapacitiesApi.middleware,
  permissionsApi.middleware,
  technicalConditionsApi.middleware,
  projectDocumentationAvailabilitiesApi.middleware,
  projectKindsApi.middleware,
  technicalConditionsActTypesApi.middleware,
  meteringPointProjectsApi.middleware,
  notificationTypesApi.middleware,
  economicSectorsApi.middleware,
  gasSuppliersApi.middleware,
  reportsApi.middleware,
  reportSourceTypesApi.middleware,
  disconnectionNotificationApi.middleware,
  executionResultsApi.middleware,
  guaranteeLettersApi.middleware,
  meteringDevicesCommercialApi.middleware,
  meteringModemCommercialApi.middleware,
  gasConsumingEquipmentCommercialApi.middleware,
  meteringNodeCommercialApi.middleware,
  meteringSealCommercialApi.middleware,
  violationDecisionsApi.middleware,
  violationMeasuresApi.middleware,
  violationResultsApi.middleware,
  violationTypesApi.middleware,
  violationActsApi.middleware,
  counterpartyContactPersonsApi.middleware,
  counterpartyPositionsApi.middleware,
  orderedCapacityExcessesApi.middleware,
  gasDistributionReportsApi.middleware,
  lastResortSuppliersApi.middleware,
  meteringDeviceConsumerIndicatorApi.middleware,
  distributionTariffsApi.middleware,
  actualExpenseTransferApi.middleware,
  consumerDocumentsApi.middleware,
  appendix4Api.middleware,
  orderedCapacityFinalExcessApi.middleware,
  apiKeysApi.middleware,
  workingPeriodsApi.middleware,
  ssoIntegrationsApi.middleware,
  auditApi.middleware,
  subsystemsApi.middleware,
  referenceBooksApi.middleware,
  reportTemplatesApi.middleware,
  employeePositionApi.middleware,
  departmentsApi.middleware,
  meteringDeviceManufacturersAPI.middleware,
  meterTypesApi.middleware,
  meterKindsApi.middleware,
  meterAccuracyClassesApi.middleware,
];

const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(middlewares),
});

export default store;

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
